"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import type { SyncStatus, SyncStatusType } from "../types/sync-progress"
import { SYNC_CONFIG } from "../constants/sync-progress"
import { useLocalStorageCleanup } from "./useLocalStorageCleanup"

const INITIAL_SYNC_STATUS: SyncStatus = {
  progress: 0,
  status: "initializing",
  message: "Initializing sync...",
  recordsProcessed: 0,
  totalRecords: SYNC_CONFIG.totalRecords,
  errors: [],
}

export const useSyncProgress = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(INITIAL_SYNC_STATUS)
  const intervalRef = useRef<number | null>(null)
  const { cleanupFlowData, markFlowCompleted, getSyncingStatus } = useLocalStorageCleanup()

  const clearInterval = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const updateProgress = useCallback(
    (currentProgress: number, statusType: string) => {
      const recordsProcessed = Math.floor((currentProgress / 100) * SYNC_CONFIG.totalRecords)

      if (currentProgress >= 100) {
        setSyncStatus({
          progress: 100,
          status: "completed" as SyncStatusType,
          message: `Sync ${statusType} successfully!`,
          recordsProcessed: SYNC_CONFIG.totalRecords,
          totalRecords: SYNC_CONFIG.totalRecords,
          errors: [],
        })

        // Cleanup and mark as completed
        cleanupFlowData()
        markFlowCompleted()
        clearInterval()
      } else {
        setSyncStatus((prev) => ({
          ...prev,
          progress: currentProgress,
          status: "Saved and Active Syncing" as SyncStatusType,
          message: `Syncing data... ${Math.floor(currentProgress)}%`,
          recordsProcessed,
        }))
      }
    },
    [cleanupFlowData, markFlowCompleted, clearInterval],
  )

  const simulateSync = useCallback(() => {
    let currentProgress = 0
    const statusType = getSyncingStatus()

    intervalRef.current = window.setInterval(() => {
      const increment =
        Math.random() * (SYNC_CONFIG.maxProgressIncrement - SYNC_CONFIG.minProgressIncrement) +
        SYNC_CONFIG.minProgressIncrement

      currentProgress += increment
      updateProgress(100, statusType)
      // updateProgress(Math.min(currentProgress, 100), statusType)
    }, SYNC_CONFIG.updateInterval)
  }, [getSyncingStatus, updateProgress])

  const resetSync = useCallback(() => {
    clearInterval()
    setSyncStatus(INITIAL_SYNC_STATUS)
  }, [clearInterval])

  useEffect(() => {
    simulateSync()

    // Cleanup on unmount
    return () => {
      clearInterval()
    }
  }, [simulateSync, clearInterval])

  return {
    syncStatus,
    resetSync,
  }
}
